import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Input, Label, Modal, SearchField } from '@heroui/react';

import Notice from '../../common/Notice';
import UserBadge from '../../common/UserBadge';
import { AdminChip } from '../../common/StatusChip';
import { LoadMore, EmptyResult } from '../../common/LoadMore';
import { SkeletonText } from '../../common/Skeleton';
import usersService from '../../../services/users.service';
import { getUser } from '../../../services/session';
import { USERS, messageOf } from '../../../i18n/el';

// Οθόνη μελών της ομάδας (Πίνακας 1, `user:manage`).
//
// **Ποιος τη βλέπει, και από ποιο κείμενο.** Ο Πίνακας 1 δεν έχει γραμμή «προβολή
// χρηστών»· έχει «διαχείριση λογαριασμών χρηστών και καθολικών ρόλων», που ανήκει
// στον διαχειριστή. Η απαρίθμηση κρίθηκε **μέρος της διαχείρισης** και όχι χωριστό
// δικαίωμα: δεν υπάρχει τρόπος να διαχειριστεί κανείς λογαριασμούς που δεν βλέπει.
// Η εναλλακτική —κάθε ταυτοποιημένος βλέπει τον κατάλογο— απορρίφθηκε, γιατί θα
// έδινε σε κάθε μέλος τις διευθύνσεις ηλεκτρονικού ταχυδρομείου όλων χωρίς καμία
// απαίτηση να το ζητά.
//
// Ο πραγματικός φράχτης είναι ο **εξυπηρετητής**: ολόκληρος ο δρομολογητής των
// χρηστών απαιτεί διαχειριστή. Η απόκρυψη της εγγραφής από το μενού είναι
// ευχρηστία, όχι ασφάλεια — ίδιο σκεπτικό με την οθόνη των κλειδιών.
//
// **Κανένας κανόνας δεν αντιγράφεται εδώ.** Ποιος επιτρέπεται να υποβιβαστεί —όχι
// ο εαυτός, όχι ο τελευταίος διαχειριστής— κρίνεται στο API κατά το §4.4. Η οθόνη
// κρύβει την ενέργεια στον ίδιο τον συνδεδεμένο, όπου δεν έχει ποτέ νόημα, και
// **εμφανίζει την άρνηση** σε κάθε άλλη περίπτωση αντί να προσπαθεί να τη μαντέψει:
// το πλήθος των διαχειριστών δεν είναι γνωστό στον πελάτη, και μια πρόβλεψη
// βασισμένη στη φορτωμένη σελίδα θα ήταν λάθος στη σελίδα 2.

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [meta, setMeta] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [term, setTerm] = useState('');
    // Ο όρος που **έχει σταλεί**, χωριστά από ό,τι πληκτρολογείται: αλλιώς κάθε
    // πάτημα πλήκτρου θα ζητούσε σελίδα.
    const [query, setQuery] = useState('');

    // Ο λογαριασμός του οποίου ο ρόλος μεταβάλλεται, με τη ζητούμενη τιμή.
    const [pending, setPending] = useState(null);
    const [isSaving, setSaving] = useState(false);

    const currentUserId = getUser()?.id;

    const load = useCallback(
        (page = 1) => {
            setLoading(true);
            return usersService.getUsers({ page, q: query }).then(
                ({ data, meta: pageMeta }) => {
                    // Η πρώτη σελίδα αντικαθιστά, οι επόμενες προσαρτώνται: ίδιο
                    // πρότυπο με τις υπόλοιπες σελιδοποιημένες οθόνες.
                    setUsers((current) => (page === 1 ? data : [...current, ...data]));
                    setMeta(pageMeta);
                    setError('');
                    setLoading(false);
                },
                (apiError) => {
                    setError(messageOf(apiError));
                    setLoading(false);
                }
            );
        },
        [query]
    );

    useEffect(() => { load(); }, [load]);

    function submitSearch(event) {
        event.preventDefault();
        setQuery(term);
    }

    function applyRole() {
        setSaving(true);
        usersService.setAdmin(pending.user.id, pending.isAdmin).then(
            (updated) => {
                setUsers((current) =>
                    current.map((user) => (user.id === updated.id ? updated : user))
                );
                setSaving(false);
                setPending(null);
            },
            (apiError) => {
                // **Το σφάλμα μένει ΣΤΟΝ ΔΙΑΛΟΓΟ**, δίπλα στο κουμπί που το προκάλεσε.
                // Ο κατάλογος από κάτω κυλά, και μήνυμα στην κορυφή του θα μπορούσε να
                // βρεθεί εκτός οπτικού πεδίου τη στιγμή που ο χρήστης περιμένει
                // απάντηση. Εδώ ζει και η άρνηση του τελευταίου διαχειριστή, που είναι
                // η μόνη που ο χρήστης δεν μπορεί να προβλέψει.
                setPending((current) => current && { ...current, error: messageOf(apiError) });
                setSaving(false);
            }
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <Card>
                <Card.Header><Card.Title>{USERS.title}</Card.Title></Card.Header>
                <Card.Content className="flex flex-col gap-4">
                    <p className="text-sm text-muted">{USERS.intro}</p>
                    <p className="text-sm text-muted">{USERS.noCreate}</p>

                    <form onSubmit={submitSearch} className="flex items-end gap-2">
                        <SearchField value={term} onChange={setTerm} className="flex-1">
                            <Label>{USERS.search}</Label>
                            <Input placeholder={USERS.searchPlaceholder} />
                        </SearchField>
                        <Button type="submit" size="sm">{USERS.search}</Button>
                    </form>

                    {error && (
                        <Notice status="danger" title={USERS.loadError} onDismiss={() => setError('')}>
                            {error}
                        </Notice>
                    )}

                    {isLoading && users.length === 0 && <SkeletonText />}

                    {!isLoading && users.length === 0 && !error && (
                        // Το `isSearching` δηλώνει το **εύρος του ισχυρισμού**: με ενεργό
                        // όρο, «καμία αντιστοιχία»· χωρίς, το μήνυμα του κενού καταλόγου.
                        // Η αναζήτηση γίνεται στον διακομιστή, οπότε και τα δύο είναι
                        // αληθή για ολόκληρο το σύνολο και όχι για τη φορτωμένη σελίδα.
                        <EmptyResult isSearching={Boolean(query)} emptyMessage={USERS.empty} />
                    )}

                    <ul className="flex flex-col gap-2">
                        {users.map((user) => {
                            const isSelf = user.id === currentUserId;

                            return (
                                <li
                                    key={user.id}
                                    className="flex flex-wrap items-center gap-2 rounded-xl border border-border p-3"
                                >
                                    <div className="min-w-0 flex-1">
                                        <UserBadge user={user} />
                                    </div>

                                    <AdminChip isAdmin={user.isAdmin} />

                                    {/* Στον ίδιο τον συνδεδεμένο δεν αποδίδεται ενέργεια:
                                        ο υποβιβασμός του εαυτού απορρίπτεται πάντοτε από
                                        το API, και κουμπί που αρνείται πάντα είναι
                                        χειρότερο από απουσία κουμπιού. */}
                                    {isSelf ? (
                                        <span className="text-sm text-muted">{USERS.you}</span>
                                    ) : (
                                        <Button
                                            size="sm"
                                            variant={user.isAdmin ? 'outline' : 'secondary'}
                                            onPress={() => setPending({ user, isAdmin: !user.isAdmin })}
                                        >
                                            <i
                                                className={user.isAdmin
                                                    ? 'fa-solid fa-shield-xmark'
                                                    : 'fa-solid fa-shield-halved'}
                                                aria-hidden="true"
                                            />
                                            {user.isAdmin ? USERS.demote : USERS.promote}
                                        </Button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    <LoadMore
                        loaded={users.length}
                        meta={meta}
                        isLoading={isLoading}
                        onLoadMore={() => load((meta?.page ?? 1) + 1)}
                        noun={USERS.noun}
                    />
                </Card.Content>
            </Card>

            {/* ⚠ **Υπό συνθήκη, με `isOpen` σκέτο** (D26). Το `pending` μηδενίζεται
                μετά από **επιτυχή** μεταβολή, δηλαδή από πηγή εκτός του διαλόγου, και
                η βιβλιοθήκη αγνοεί το prop όταν γίνεται ψευδές έτσι. */}
            {pending && (
            <Modal.Root isOpen onOpenChange={() => setPending(null)}>
                <Modal.Backdrop>
                    <Modal.Container size="sm">
                        <Modal.Dialog>
                            <Modal.Header>
                                <Modal.Heading>
                                    {pending.isAdmin ? USERS.promoteTitle : USERS.demoteTitle}
                                </Modal.Heading>
                                <Modal.CloseTrigger />
                            </Modal.Header>
                            <Modal.Body className="flex flex-col gap-3">
                                <UserBadge user={pending.user} />
                                <p className="text-sm">
                                    {pending.isAdmin ? USERS.promoteBody : USERS.demoteBody}
                                </p>
                                {pending.error && (
                                    <Notice status="danger" title={USERS.actionError}>
                                        {pending.error}
                                    </Notice>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="outline" onPress={() => setPending(null)}>
                                    {USERS.cancel}
                                </Button>
                                <Button
                                    variant={pending.isAdmin ? 'primary' : 'danger'}
                                    onPress={applyRole}
                                    isPending={isSaving}
                                >
                                    {USERS.confirm}
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal.Root>
            )}
        </div>
    );
}
